/**
 * PriorityControl — compact queue-priority picker for bank table rows.
 *
 * A queue "importance" rank: LOWER number = higher priority = promoted into a
 * free slot SOONER. 1 = highest, 10 = lowest. "—" (null) = unprioritised
 * (promoted last). Rendered as a small native <select> for a table cell.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { cn } from '@/lib/utils';

export interface PriorityControlProps {
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  /** Highest selectable priority (default 10). */
  max?: number;
}

export const PriorityControl: FC<PriorityControlProps> = ({
  value,
  onChange,
  disabled = false,
  max = 10,
}) => {
  // 1 (highest) at the top → 10 (lowest) at the bottom.
  const options = Array.from({ length: max }, (_, i) => i + 1);
  return (
    <select
      value={value ?? ''}
      disabled={disabled}
      aria-label="Queue priority (1 = highest, promoted first)"
      title="Queue priority — 1 is the highest (promoted into a free slot first)"
      onChange={(e) =>
        onChange(e.target.value === '' ? null : Number(e.target.value))
      }
      className={cn(
        'h-6 w-20 rounded border border-input bg-background px-1 text-[11px] tabular-nums',
        'focus:outline-none focus:ring-1 focus:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      <option value="">—</option>
      {options.map((n) => (
        <option key={n} value={n}>
          {n === 1 ? '1 (highest)' : n === max ? `${n} (lowest)` : n}
        </option>
      ))}
    </select>
  );
};

export default PriorityControl;
